import { useCallback, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Coins, Download, IndianRupee, Search, Users, Wallet } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SummaryCard } from '@/components/SummaryCard';
import { usePaymentsManagementQuery } from '@/api/query/payment';
import { PaymentManagementAPIResponseMapper } from '@/api/mapper/payment';
import { getPaymentsManagement } from '@/api/service/payment';
import { useAppContext } from '@/contexts/AppContext';
import type { PaymentMemberRow, Policy } from '@/types';
import { formatCurrency } from '@/lib/payments';
import { PaymentTable } from './PaymentTable';
import { createColumns, type ColumnSort, type SortField } from './TableColumns';
import { useTableFirstScroll } from '@/hooks/useTableFirstScroll';
import { useTableQuery } from '@/hooks/useTableQuery';
import { nextSortDirection } from '@/components/DataTable';
import { displayMemberNo } from '@/lib/utils';
import { useExpandedMembers } from './useExpandedMembers';

type StatusFilter = 'all' | 'paid' | 'unpaid';

const FILTERS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'paid', label: 'Paid This Month' },
  { id: 'unpaid', label: 'Unpaid This Month' },
];

const isLive = (policy: Policy) => policy.status !== 'closed' && policy.thisMonth !== 'no_dues';

const matchesFilter = (row: PaymentMemberRow, filter: StatusFilter) => {
  const live = row.policies.filter(isLive);
  if (filter === 'unpaid') return live.some((policy) => policy.thisMonth === 'unpaid');
  if (filter === 'paid') return live.length > 0 && live.every((policy) => policy.thisMonth === 'paid');
  return true;
};

const policyMatches = (policy: Policy, term: string) =>
  policy.policyNo.toLowerCase().includes(term) ||
  Boolean(policy.cardNo?.includes(term)) ||
  Boolean(policy.loan.loanNo?.toLowerCase().includes(term));

const downloadCsv = (rows: PaymentMemberRow[]) => {
  const header = ['Member ID', 'Card No', 'Name', 'Phone', 'Policy No', 'Status', 'This Month', 'Months Paid', 'Total Paid', 'Due', 'Loan No', 'Loan Outstanding'];
  const lines = rows.flatMap(({ member, policies }) =>
    policies.map((policy) =>
      [
        displayMemberNo(member),
        policy.cardNo ?? '',
        member.name,
        member.phone,
        policy.policyNo,
        policy.status,
        policy.thisMonth,
        policy.paidMonths,
        policy.totalPaid,
        policy.amountDue,
        policy.loan.loanNo ?? '',
        policy.loan.outstanding ?? '',
      ].join(',')
    )
  );

  const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'payments_report.csv';
  anchor.click();
  window.URL.revokeObjectURL(url);
};

export default function PaymentManagement() {
  const { pageRef, tableLocked } = useTableFirstScroll();
  const { isAuthenticated } = useAppContext();
  const { searchTerm, term, setSearchTerm, filter: statusFilter, setFilter: setStatusFilter, page, setPage, pageSize, setPageSize, pageQuery } =
    useTableQuery<StatusFilter>('all');
  // ID sort: null = the order the API sends (also after a reload).
  const [sort, setSort] = useState<ColumnSort | null>(null);
  const { openIds, entering, leaving, toggle } = useExpandedMembers();
  // Policy Status header choice: UI only for now, the rows aren't filtered by it.
  const [policyStatus, setPolicyStatus] = useState('');

  const { data, isLoading, isFetching, error } = usePaymentsManagementQuery(pageQuery, isAuthenticated);
  const totalMembers = data?.total ?? 0;
  const overview = data?.overview;

  // The backend searches; filter and sort apply to the members of the current page.
  const pageRows = useMemo(() => {
    const rows = (data?.rows ?? []).filter((row) => matchesFilter(row, statusFilter));
    if (!sort) return rows;
    const direction = sort.direction === 'asc' ? 1 : -1;
    return rows.sort((a, b) => a.member.id.localeCompare(b.member.id, undefined, { numeric: true }) * direction);
  }, [data, statusFilter, sort]);
  const isNarrowed = statusFilter !== 'all';

  // A search that matches a member's 2nd+ policy (or its loan) opens that member's policies.
  const visibleOpenIds = useMemo(() => {
    if (!term) return openIds;
    const matched = pageRows
      .filter((row) => row.policies.slice(1).some((policy) => policyMatches(policy, term)))
      .map((row) => row.member.id);
    return new Set([...Array.from(openIds), ...matched]);
  }, [openIds, pageRows, term]);

  const handleSort = useCallback((field: SortField) => {
    setSort((current) => {
      const direction = nextSortDirection(current?.field === field ? current.direction : null);
      return direction ? { field, direction } : null;
    });
  }, []);

  // Export every member (all pages) that matches the current search and filter.
  const [isExporting, setIsExporting] = useState(false);
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await getPaymentsManagement({ skip: 0, limit: Math.max(totalMembers, 1), search: pageQuery.search });
      const rows = (response.data ?? []).map(PaymentManagementAPIResponseMapper);
      downloadCsv(rows.filter((row) => matchesFilter(row, statusFilter)));
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo(
    () => createColumns(sort, handleSort, { onToggle: toggle }, { value: policyStatus, onChange: setPolicyStatus }),
    [sort, handleSort, toggle, policyStatus]
  );

  return (
    <div ref={pageRef} data-table-locked={tableLocked} className="group/page flex h-full flex-col overflow-y-auto p-5">
      <div className="mb-6 shrink-0">
        <div className="mb-4 flex items-center gap-2">
          <h1 className="text-lg font-bold text-gray-900">Payment Management</h1>
          {isFetching && !isLoading && (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#4369a0]/25 border-t-[#4369a0]" />
          )}
        </div>
        <p className="-mt-3 mb-4 text-sm text-gray-500">Track policy payments, loans, dues and collection status</p>
        <div className="h-px bg-gray-200/80" />
      </div>

      <div className="mb-5 grid shrink-0 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        <SummaryCard
          icon={Users}
          tone="gray"
          label="Total Members"
          value={isLoading ? '--' : String(overview?.totalMembers ?? 0)}
          rows={[
            ['Policies', String(overview?.totalPolicies ?? 0)],
            ['Members With Loan', String(overview?.membersWithLoan ?? 0)],
          ]}
        />
        <SummaryCard
          icon={CheckCircle2}
          tone="emerald"
          label="Paid This Month"
          value={isLoading ? '--' : String((overview?.policyPaidThisMonth ?? 0) + (overview?.loanPaidThisMonth ?? 0))}
          rows={[
            ['Policy', String(overview?.policyPaidThisMonth ?? 0)],
            ['Loan', String(overview?.loanPaidThisMonth ?? 0)],
          ]}
        />
        <SummaryCard
          icon={AlertCircle}
          tone="amber"
          label="Unpaid This Month"
          value={isLoading ? '--' : String((overview?.policyUnpaidThisMonth ?? 0) + (overview?.loanUnpaidThisMonth ?? 0))}
          rows={[
            ['Policy', `${overview?.policyUnpaidThisMonth ?? 0} · ${formatCurrency(overview?.policyPendingAmount ?? 0)}`],
            ['Loan', `${overview?.loanUnpaidThisMonth ?? 0} · ${formatCurrency(overview?.loanDueAmount ?? 0)}`],
          ]}
        />
        <SummaryCard
          icon={IndianRupee}
          tone="brand"
          label="Total Collected"
          value={isLoading ? '--' : formatCurrency((overview?.policyCollected ?? 0) + (overview?.loanRepaid ?? 0))}
          rows={[
            ['Policy', formatCurrency(overview?.policyCollected ?? 0)],
            ['Loan Repaid', formatCurrency(overview?.loanRepaid ?? 0)],
          ]}
        />
        <SummaryCard
          icon={Wallet}
          tone="gray"
          label="Loan Outstanding"
          value={isLoading ? '--' : formatCurrency(overview?.loanOutstanding ?? 0)}
          rows={[
            ['Active Loans', String(overview?.loansActive ?? 0)],
            ['Given', formatCurrency(overview?.loansGiven ?? 0)],
          ]}
        />
        <SummaryCard
          icon={Coins}
          tone="gray"
          label="Member Balance"
          value={isLoading ? '--' : formatCurrency(overview?.memberBalanceDue ?? 0)}
          rows={[
            ['Members With Balance', String(overview?.membersWithBalance ?? 0)],
            ['Interest Charged', formatCurrency(overview?.balanceInterestCharged ?? 0)],
          ]}
        />
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Unable to load payments. Please try again.
        </div>
      )}

      <Card className="flex h-full shrink-0 flex-col border-gray-200/80 shadow-sm">
        <CardContent className="flex min-h-0 flex-1 flex-col p-5">
          <div className="mb-4 flex shrink-0 flex-wrap items-center gap-3">
            <h2 className="whitespace-nowrap text-sm font-semibold text-gray-900">
              Payments
              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {isNarrowed ? pageRows.length : totalMembers}
              </span>
            </h2>

            <div className="relative min-w-[220px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by ID, card no, name, phone, Aadhaar, policy or loan no..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9 border-gray-200 bg-white pl-9"
              />
            </div>

            <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1">
              {FILTERS.map((filter) => (
                <Button
                  key={filter.id}
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setStatusFilter(filter.id)}
                  className={`h-8 px-3 text-xs ${
                    statusFilter === filter.id
                      ? 'bg-[#4369a0]/10 font-medium text-[#4369a0] hover:bg-[#4369a0]/15'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {filter.label}
                </Button>
              ))}
            </div>

            <Button
              variant="outline"
              onClick={handleExport}
              disabled={isExporting}
              className="h-9 whitespace-nowrap border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            >
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
            {isLoading ? (
              <div className="flex h-full flex-col items-center justify-center rounded-xl border border-gray-200/80 bg-white">
                <span className="h-8 w-8 animate-spin rounded-full border-2 border-[#4369a0]/30 border-t-[#4369a0]" />
                <p className="mt-3 text-sm text-gray-500">Loading payments...</p>
              </div>
            ) : (
              <PaymentTable
                rows={pageRows}
                columns={columns}
                openIds={visibleOpenIds}
                enteringId={entering}
                leavingId={leaving}
                currentPage={page}
                pageSize={pageSize}
                totalItems={totalMembers}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
